import axios from "axios";
import humps from "humps";
import qs from "qs";
import store from "@/store/index";

export const axiosApi = axios.create({
  baseURL: `${process.env.VUE_APP_API_BASE_URL}/api/v1/`,
  headers: {
    common: {
      Authorization: `Bearer ${localStorage.getItem("NBP-Auth-Token")}`,
      Refresh: `${localStorage.getItem("NBP-Refresh-Token")}`
    }
  },
  paramsSerializer: params =>
    qs.stringify(humps.decamelizeKeys(params), { arrayFormat: "brackets" }),
  transformResponse: [
    ...axios.defaults.transformResponse,
    data => (data instanceof Blob ? data : humps.camelizeKeys(data))
  ],
  transformRequest: [
    data => (data instanceof FormData ? data : humps.decamelizeKeys(data)),
    ...axios.defaults.transformRequest
  ]
});

async function renewToken(config) {
  const res = await axios({
    ...config,
    method: "post",
    url: "sessions/refresh_token",
    params: {}
  });

  store.commit("auth/SET_TOKEN", res.data);
  authorizeAxios(res.data);
  config.headers.Authorization = "Bearer " + res.data.token;
  config.headers.Refresh = res.data.refreshToken || "";
  return config.headers;
}

let refreshingFunc = undefined;

export function injectAuthInterceptor() {
  async function checkToken(config) {
    const {token, tokenExp} = store.state.auth
    if (token) {
      if (tokenExp && tokenExp * 1000 < +new Date()) {
        try {
          if (!refreshingFunc) refreshingFunc = renewToken(config);
          const headers = await refreshingFunc;
          return {...config, headers}
        } catch (err) {
          await store.dispatch("auth/logout");
        } finally {
          refreshingFunc = undefined;
        }
      }
    }
    return config
  }


  axiosApi.interceptors.request.use(
    (config) => {
      return checkToken(config)
    },
    (error) => {
      Promise.reject(error)
    }
  )


  axiosApi.interceptors.response.use(undefined, async error => {
    if (error.response.status === 401) {
      if (!store.state.auth.token) {
        return Promise.reject(error);
      }
      if (error.response.data.errors === "Token wygasł.") {
        // previous method of error handling, left just in case (now it's handled before request)
        const originalConfig = error.config;

        try {
          // the trick here, that `refreshingFunc` is global, e.g. 2 expired requests will get the same function pointer and await same function.
          if (!refreshingFunc) refreshingFunc = renewToken(originalConfig);

          await refreshingFunc;

          return await axios.request(originalConfig); // retry original request
        } catch (err) {
          store.dispatch("auth/logout");
        } finally {
          refreshingFunc = undefined;
        }
      } else {
        store.dispatch("auth/logout");
        store.dispatch("snackbar/showSnackbar", {
          message: ["Zaloguj się ponownie"],
          type: "error"
        });
      }
    } else if (error.response.status === 403) {
      store.dispatch("snackbar/showSnackbar", {
        message: error.response.data.errors,
        type: "error"
      });
    } else if (error.response.status === 404) {
      if (!error.config?.skipNotFoundSnackbar) {
        store.dispatch("snackbar/showSnackbar", {
          message: error.response.data.errors || ["Nie znaleziono"],
          type: "error"
        });
      }
    } else if (error.response.status === 409) {
      store.dispatch("snackbar/showSnackbar", {
        message: error.response.data.errors,
        type: "error"
      });
    } else if (error.response.status === 422) {
      store.dispatch("snackbar/showSnackbar", {
        message: error.response.data.errors,
        type: "error"
      });
    } else {
      store.dispatch("snackbar/showSnackbar", {
        message: ["Coś poszło nie tak, spróbuj ponownie."],
        type: "error"
      });
    }
    return Promise.reject(error);
  });
}

export function authorizeAxios({ token, refreshToken }) {
  axiosApi.defaults.headers.common.Authorization = `Bearer ${token}`;
  axiosApi.defaults.headers.common.Refresh = refreshToken;
}

export function deauthorizeAxios() {
  delete axiosApi.defaults.headers.common.Authorization;
  delete axiosApi.defaults.headers.common.Refresh;
}

export function createCRUD(endpoint) {
  return {
    show(id) {
      return axiosApi.get(`/${endpoint}/${id}`);
    },
    index(params) {
      return axiosApi.get(`/${endpoint}`, { params });
    },
    delete(id) {
      return axiosApi.delete(`/${endpoint}/${id}`);
    },
    create(params) {
      return axiosApi.post(`/${endpoint}`, params);
    },
    update(id, params) {
      return axiosApi.patch(`/${endpoint}/${id}`, params);
    }
  };
}
